import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ShareIcon from '@mui/icons-material/Share';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DataGrid, GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { IAdminDashboardComponent, deleteAdminDashboardComponent, useAdminUserDashboardComponentsQuery } from '../../api/admin';
import AddDashboardComponentDialog from './AddDashboardComponentDialog';
import ShareDashboardDialog from './ShareDashboardDialog';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

interface Props {
	userId: number;
	/** Fill the parent (a flex column) instead of using a fixed viewport-based height. */
	fill?: boolean;
}

export default function UserDashboardTable({ userId, fill }: Props) {
	const queryClient = useQueryClient();
	const { data: components, isLoading } = useAdminUserDashboardComponentsQuery(userId);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [shareDialogOpen, setShareDialogOpen] = useState(false);
	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
	const [deletingId, setDeletingId] = useState<number | null>(null);

	const selectedComponents: IAdminDashboardComponent[] = (components ?? []).filter((c) =>
		selectionModel.includes(c.id)
	);

	async function handleDelete(componentId: number) {
		setDeletingId(componentId);
		try {
			await deleteAdminDashboardComponent(userId, componentId);
			await queryClient.invalidateQueries(['admin', 'users', userId, 'dashboard-components']);
		} finally {
			setDeletingId(null);
		}
	}

	const columns: GridColDef[] = [
		{
			field: 'order',
			headerName: 'Order',
			width: 80,
			type: 'number',
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 1,
			minWidth: 150,
		},
		{
			field: 'size',
			headerName: 'Size',
			width: 100,
		},
		{
			field: 'info',
			headerName: 'Info',
			flex: 2,
			minWidth: 200,
		},
		{
			field: 'actions',
			headerName: '',
			width: 50,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams) => (
				<IconButton
					size="small"
					color="error"
					disabled={deletingId === params.row.id}
					onClick={() => handleDelete(params.row.id)}
				>
					{deletingId === params.row.id
						? <CircularProgress size={16} color="error" />
						: <DeleteOutlineIcon fontSize="small" />}
				</IconButton>
			),
		},
	];

	return (
		<Box sx={fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { height: 'calc(100vh - 310px)', minHeight: 300 }}>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
				{selectionModel.length > 0 && (
					<Button
						variant="contained"
						size="small"
						startIcon={<ShareIcon />}
						onClick={() => setShareDialogOpen(true)}
					>
						Share {selectionModel.length} Component{selectionModel.length !== 1 ? 's' : ''}
					</Button>
				)}
				<Button variant="outlined" size="small" onClick={() => setDialogOpen(true)}>
					Add Component
				</Button>
			</Box>
			<DataGrid
				sx={fill ? { flex: 1, minHeight: 0 } : undefined}
				rows={components ?? []}
				columns={columns}
				loading={isLoading}
				checkboxSelection
				disableSelectionOnClick
				selectionModel={selectionModel}
				onSelectionModelChange={setSelectionModel}
				components={{ Toolbar: AdminQuickFilterToolbar }}
				sortModel={[{ field: 'order', sort: 'asc' }]}
				density="compact"
			/>
			<AddDashboardComponentDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				userId={userId}
			/>
			<ShareDashboardDialog
				open={shareDialogOpen}
				onClose={() => {
					setShareDialogOpen(false);
					setSelectionModel([]);
				}}
				components={selectedComponents}
				sourceUserId={userId}
			/>
		</Box>
	);
}
